import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type DocumentType = "employee" | "applicant";
type AccessAction = "preview_opened" | "preview_failed" | "download_requested";
type AccessSource = "modal" | "preview_route" | "download_button";

interface LogAccessParams {
  documentId: string;
  documentType: DocumentType;
  action: AccessAction;
  source: AccessSource;
}

/**
 * DSGVO-konformer Audit-Log Hook für Dokumentenzugriffe
 * 
 * Loggt nur Metadaten:
 * - Wer (user_id, user_role)
 * - Was (document_id, document_type) - KEINE Dateinamen
 * - Wann (timestamp)
 * - Wie (action, source)
 * 
 * WICHTIG:
 * - Fehler im Logging blockieren NICHT die Vorschau
 * - Logging erfolgt nur bei erfolgreicher Authentifizierung
 * - Keine sensiblen Daten (URLs, Dateinamen, Inhalte) werden geloggt
 */
export function useDocumentAccessLog() {
  const { user, userRole } = useAuth();

  const logAccess = useCallback(
    async ({ documentId, documentType, action, source }: LogAccessParams): Promise<void> => {
      // Nur loggen wenn authentifiziert und document_id vorhanden
      if (!user?.id || !documentId) {
        console.debug("[DocumentAccessLog] Skipped: No user or document ID");
        return;
      }

      try {
        const { error } = await supabase.from("document_access_log").insert({
          user_id: user.id,
          user_role: userRole || "unknown",
          document_id: documentId,
          document_type: documentType,
          action,
          source,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });

        if (error) {
          // Logging-Fehler nur in Console, NICHT dem User zeigen
          console.error("[DocumentAccessLog] Insert failed:", error.message);
        } else {
          console.debug("[DocumentAccessLog] Logged:", { documentId, action, source });
        }
      } catch (err) {
        // Niemals die Vorschau blockieren
        console.error("[DocumentAccessLog] Error:", err);
      }
    },
    [user?.id, userRole]
  );

  /**
   * Convenience-Methode für Preview-Öffnung
   */
  const logPreviewOpened = useCallback(
    (documentId: string, documentType: DocumentType, source: AccessSource) => {
      logAccess({ documentId, documentType, action: "preview_opened", source });
    },
    [logAccess]
  );

  /**
   * Convenience-Methode für fehlgeschlagene Preview
   */
  const logPreviewFailed = useCallback(
    (documentId: string, documentType: DocumentType, source: AccessSource) => {
      logAccess({ documentId, documentType, action: "preview_failed", source });
    },
    [logAccess]
  );

  /**
   * Convenience-Methode für Download-Request
   */
  const logDownloadRequested = useCallback(
    (documentId: string, documentType: DocumentType, source: AccessSource) => {
      logAccess({ documentId, documentType, action: "download_requested", source });
    },
    [logAccess]
  );

  return {
    logAccess,
    logPreviewOpened,
    logPreviewFailed,
    logDownloadRequested,
  };
}
